<template>
  <div class="admin-page">
    <el-card shadow="never">
<p class="desc">
        从服务器配置读取 MySQL 连接信息，执行 mysqldump 并打包为 ZIP 下载。仅超级管理员可用。
      </p>

      <el-alert
        v-if="!isSuperAdmin"
        type="warning"
        title="当前账号无权限"
        description="需要超级管理员（superadmin）角色才能下载数据库备份。"
        show-icon
        :closable="false"
      />

      <div v-else class="actions">
        <el-button type="primary" :loading="downloadingFull" @click="downloadBackup('/api/v1/mysql/backup', 'full')">
          下载全量备份（ZIP）
        </el-button>
        <el-button type="success" :loading="downloadingSchema" @click="downloadBackup('/api/v1/mysql/backup/schema', 'schema')">
          下载结构备份（ZIP）
        </el-button>
        <p class="hint">结构备份仅包含建表语句、存储过程等，不含数据行。</p>
        <p v-if="lastFile" class="hint">上次下载：{{ lastFile }}</p>
      </div>
    </el-card>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import axios from 'axios'
import { ElMessage } from 'element-plus'
import { isSuperAdmin as checkSuperAdmin } from '@/utils/sysRole'

export default {
  name: 'DatabaseBackupView',
  setup() {
    const downloadingFull = ref(false)
    const downloadingSchema = ref(false)
    const lastFile = ref('')

    const isSuperAdmin = computed(() => {
      try {
        const user = JSON.parse(localStorage.getItem('user_info') || '{}')
        return checkSuperAdmin(user.sysRole)
      } catch {
        return false
      }
    })

    const downloadBackup = async (url, kind) => {
      if (!isSuperAdmin.value) {
        ElMessage.warning('需要超级管理员权限')
        return
      }

      const loadingRef = kind === 'schema' ? downloadingSchema : downloadingFull
      loadingRef.value = true
      try {
        const token = localStorage.getItem('jwt_token')
        const response = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
          timeout: 600000
        })

        const disposition = response.headers['content-disposition'] || ''
        let filename = kind === 'schema'
          ? `coolpeople_schema_${Date.now()}.zip`
          : `coolpeople_backup_${Date.now()}.zip`
        const match = disposition.match(/filename="?([^";\n]+)"?/)
        if (match) {
          filename = decodeURIComponent(match[1])
        }

        const blob = new Blob([response.data], { type: 'application/zip' })
        const objectUrl = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = objectUrl
        link.download = filename
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(objectUrl)

        lastFile.value = filename
        ElMessage.success(kind === 'schema' ? '结构备份下载已开始' : '全量备份下载已开始')
      } catch (error) {
        console.error('backup download failed:', error)
        if (error.response?.data instanceof Blob) {
          try {
            const text = await error.response.data.text()
            const json = JSON.parse(text)
            ElMessage.error(json.message || '备份失败')
            return
          } catch {
            // ignore
          }
        }
        ElMessage.error(error.response?.data?.message || error.message || '备份下载失败')
      } finally {
        loadingRef.value = false
      }
    }

    onMounted(() => {
      if (!isSuperAdmin.value) {
        ElMessage.warning('需要超级管理员权限访问此页面')
      }
    })

    return {
      downloadingFull,
      downloadingSchema,
      lastFile,
      isSuperAdmin,
      downloadBackup
    }
  }
}
</script>

<style scoped>

.database-backup {
  max-width: 720px;
}
.card-header {
  font-weight: 600;
}
.desc {
  color: var(--el-text-color-regular);
  line-height: 1.6;
  margin: 0 0 20px;
}
.actions {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
}
.hint {
  margin: 0;
  font-size: 13px;
  color: var(--el-text-color-secondary);
}

</style>
